import type { QueryClient } from "@tanstack/react-query";
import { defaultEventDate, eventDatesBetween, festivalDayIso } from "@produce/shared";
import { getMapboxToken } from "@/lib/mapbox";
import {
  allCrewMembersForDayQuery,
  assignmentCountQuery,
  assignmentsForDayQuery,
  briefingDayDoorsQuery,
  briefingQuery,
  briefingStagesQuery,
  briefingViewQuery,
  crewMembersForDayQuery,
  crewMembersQuery,
  crewTrackingConsentQuery,
  crewWithAssignmentsQuery,
  lineupCountQuery,
  lineupQuery,
  mapSettingsQuery,
  producerBriefingsQuery,
  trackingFestivalAreasQuery,
} from "@/lib/queries/productionQueries";

/**
 * Warming the producer portal's production pages before the producer opens
 * them.
 *
 * The pages felt like they only started working once you clicked them, because
 * that is exactly what happened: every page mounted with an empty cache and
 * fired its own reads. This module says, per page, which reads a warm cache
 * needs — so the sidebar can start them on hover, and an open production can
 * warm its siblings while the producer is reading the current page.
 *
 * Adding a page means adding one entry to `PRODUCTION_PAGE_PLANS`; nothing
 * that calls the prefetcher changes.
 *
 * Caveat worth knowing: a plan is a second statement of what a page reads, and
 * it can silently fall behind the page. Nothing fails when it does — the page
 * simply goes back to loading on arrival. `productionPagePlans.test.ts` guards
 * the shape; the plans still have to be re-read when a page gains a query.
 */

export const PRODUCTION_PAGES = [
  "overview",
  "live-tracking",
  "crew",
  "analytics",
  "briefing",
  "schedule",
] as const;

export type ProductionPage = (typeof PRODUCTION_PAGES)[number];

interface QueryOption {
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
}

export interface WarmContext {
  productionId: string;
  /** The signed-in producer, for the account-scoped production dropdown. */
  userId?: string;
}

interface PageWarmPlan {
  /** Reads that need nothing but the production id. */
  base: QueryOption[];
  /**
   * Reads scoped to the festival day the page will open on. Split out because
   * the day itself has to be derived from the briefing first — the very
   * waterfall that makes these pages feel slow on arrival.
   */
  perDay?: (eventDate: string) => QueryOption[];
  /** The page renders a Mapbox map, which awaits the token before it builds. */
  needsMapboxToken?: boolean;
}

const PRODUCTION_PAGE_PLANS: Record<ProductionPage, (ctx: WarmContext) => PageWarmPlan> = {
  overview: ({ productionId }) => ({
    base: [
      briefingQuery(productionId),
      crewMembersQuery(productionId),
      crewWithAssignmentsQuery(productionId),
      assignmentCountQuery(productionId),
      lineupCountQuery(productionId),
    ],
    // The overview embeds a live-tracking preview map.
    needsMapboxToken: true,
  }),

  // Deliberately not warmed here: ["crew-locations", …]. It polls every five
  // seconds and is the one read that must be live on arrival, so a warm copy
  // buys nothing and would only add a request.
  "live-tracking": ({ productionId, userId }) => ({
    base: [
      producerBriefingsQuery(userId),
      mapSettingsQuery(productionId),
      trackingFestivalAreasQuery(productionId),
      crewTrackingConsentQuery(productionId),
    ],
    perDay: (eventDate) => [allCrewMembersForDayQuery(productionId, eventDate)],
    needsMapboxToken: true,
  }),

  crew: ({ productionId }) => ({
    base: [crewMembersQuery(productionId)],
  }),

  // Only the cheap first-level reads. The GPS aggregations behind them are
  // day- and time-window-scoped and genuinely live; warming those would fetch
  // a large result the page is likely to re-request anyway.
  analytics: ({ productionId, userId }) => ({
    base: [
      producerBriefingsQuery(userId),
      briefingDayDoorsQuery(productionId),
      briefingStagesQuery(productionId),
    ],
  }),

  // briefingViewQuery first: it is what the page gates its full-page spinner
  // on, so warming only the reads behind that spinner would leave the page
  // feeling exactly as slow as before.
  briefing: ({ productionId }) => ({
    base: [
      briefingViewQuery(productionId),
      briefingDayDoorsQuery(productionId),
      crewMembersQuery(productionId),
      briefingStagesQuery(productionId),
    ],
  }),

  // briefing_stages / briefing_day_doors are read by the timeline component
  // itself, not by the page — easy to miss, and without them the view-only
  // schedule still lays itself out a second time on arrival.
  schedule: ({ productionId }) => ({
    base: [
      briefingQuery(productionId),
      crewMembersQuery(productionId),
      lineupQuery(productionId),
      briefingStagesQuery(productionId),
      briefingDayDoorsQuery(productionId),
    ],
    perDay: (eventDate) => [
      assignmentsForDayQuery(productionId, eventDate),
      crewMembersForDayQuery(productionId, eventDate),
    ],
  }),
};

/** Which production page a portal path points at, if any. */
export function productionPageFromPath(
  pathname: string,
  productionId: string,
): ProductionPage | null {
  const root = `/productions/${productionId}`;
  if (pathname === root || pathname === `${root}/`) return "overview";
  if (!pathname.startsWith(`${root}/`)) return null;

  const rest = pathname.slice(root.length + 1).split("/")[0];
  return (PRODUCTION_PAGES as readonly string[]).includes(rest) ? (rest as ProductionPage) : null;
}

interface BriefingDates {
  festival_date_start?: string | null;
  festival_date_end?: string | null;
}

/**
 * The day a page will open on, without paying for a briefing fetch twice: read
 * the cached briefing when there is one, and only fetch when there isn't.
 * Returns "" when the production has no dates yet.
 */
async function resolveEventDate(
  queryClient: QueryClient,
  productionId: string,
  today: string,
): Promise<string> {
  const briefing =
    queryClient.getQueryData<BriefingDates>(briefingQuery(productionId).queryKey) ??
    (await queryClient
      .fetchQuery(briefingQuery(productionId))
      .catch(() => null as BriefingDates | null));

  if (!briefing) return "";
  return defaultEventDate(
    eventDatesBetween(briefing.festival_date_start, briefing.festival_date_end),
    today,
  );
}

/**
 * Warm one production page's reads into the cache.
 *
 * Best-effort throughout: a read that fails is left for the page to retry, and
 * never rejects this call — a prefetch must not be able to break navigation.
 * `prefetchQuery` is a no-op for data that is already fresh, so hovering the
 * same link repeatedly costs nothing.
 */
export async function prefetchProductionPage(
  queryClient: QueryClient,
  page: ProductionPage,
  ctx: WarmContext,
  today: string = festivalDayIso(),
  isAbandoned: () => boolean = () => false,
): Promise<void> {
  if (!ctx.productionId || isAbandoned()) return;

  const plan = PRODUCTION_PAGE_PLANS[page](ctx);
  const warm = (query: QueryOption) => queryClient.prefetchQuery(query).catch(() => undefined);

  // The day-scoped reads only wait on the briefing row, not on the rest of the
  // base set — making them queue behind the page's largest query would add a
  // full round-trip to the hover path this exists to shorten.
  const perDay = plan.perDay
    ? resolveEventDate(queryClient, ctx.productionId, today).then((eventDate) =>
        // Re-checked here, not only on entry: resolving the day is itself a
        // round-trip, and a sign-out during it must not be followed by a
        // fresh batch of reads.
        eventDate && !isAbandoned() ? Promise.all(plan.perDay!(eventDate).map(warm)) : undefined,
      )
    : undefined;

  await Promise.all([
    ...plan.base.map(warm),
    plan.needsMapboxToken ? getMapboxToken().catch(() => undefined) : undefined,
    perDay,
  ]);
}

/**
 * Warm every production page except the one being viewed.
 *
 * Runs one page at a time so the current page's own reads keep priority on the
 * browser's connection pool — the producer is looking at that page, not at the
 * ones being warmed behind it.
 */
export async function prefetchSiblingProductionPages(
  queryClient: QueryClient,
  currentPage: ProductionPage | null,
  ctx: WarmContext,
  today: string = festivalDayIso(),
  isAbandoned: () => boolean = () => false,
): Promise<void> {
  for (const page of PRODUCTION_PAGES) {
    // Re-checked between pages: the sweep spans seconds, and a sign-out
    // partway through must not keep reading (and caching empty answers).
    if (isAbandoned()) return;
    if (page === currentPage) continue;
    await prefetchProductionPage(queryClient, page, ctx, today, isAbandoned);
  }
}
